<template>
  <section class="statement-section">
    <div class="statement-section__header">
      <div class="statement-section__marker">3</div>
      <div>
        <h3 class="statement-section__title">
          Сведения о плательщиках <span v-if="payersRequired" class="statement-required">*</span>
        </h3>
        <p class="statement-section__description">
          Данные о плательщиках нужны для заполнения договоров на обучение или дополнительные услуги.
          В случае, если абитуриент является совершеннолетним и договор составляется на его имя, данные могут не
          заполняться.
        </p>
      </div>
    </div>

    <div class="statement-payers">
      <div v-for="(payer, index) in formData.payers" :key="payer.localId" class="statement-payer">
        <div class="statement-payer__header">
          <h4 class="statement-payer__title">
            Плательщик {{ index + 1 }}
          </h4>

          <v-btn v-if="formData.payers.length > 1" icon="mdi-delete-outline" variant="text" color="error"
            size="small" @click="$emit('remove-payer', index)" />
        </div>

        <div class="statement-fields">
          <v-autocomplete autocomplete="one-time-code"
            v-model="payer.representative"
            :items="representativeOptions"
            item-title="name"
            item-value="code"
            label="Представитель"
            variant="outlined"
            density="comfortable"
            clearable
          />
          <v-text-field autocomplete="one-time-code" :model-value="payer.lastname" :rules="optionalNameRules" label="Фамилия"
            variant="outlined" density="comfortable"
            @update:model-value="value => $emit('update-payer-name', index, 'lastname', value)" />
          <v-text-field autocomplete="one-time-code" :model-value="payer.firstname" :rules="optionalNameRules" label="Имя"
            variant="outlined" density="comfortable"
            @update:model-value="value => $emit('update-payer-name', index, 'firstname', value)" />
          <v-text-field autocomplete="one-time-code" :model-value="payer.patronymic" :rules="optionalNameRules" label="Отчество"
            variant="outlined" density="comfortable"
            @update:model-value="value => $emit('update-payer-name', index, 'patronymic', value)" />

          <v-radio-group v-model="payer.gender" class="statement-radio-group" label="Пол" inline>
            <v-radio label="Мужской" value="male" />
            <v-radio label="Женский" value="female" />
          </v-radio-group>
        </div>

        <div class="statement-inline-fields statement-inline-fields--personal">
          <v-text-field autocomplete="one-time-code"
            :model-value="payer.snils"
            label="СНИЛС"
            variant="outlined"
            density="comfortable"
            :rules="optionalSnilsRules"
            maxlength="14"
            @update:model-value="value => $emit('update-payer-snils', index, value)"
          />
          <v-text-field autocomplete="one-time-code" v-model="payer.birthDate" class="statement-date-field" type="date" variant="outlined"
            density="comfortable" :min="birthDateMin" :max="birthDateMax" label="Дата рождения" />
        </div>

        <div class="statement-inline-fields statement-inline-fields--personal">
          <v-text-field autocomplete="one-time-code"
            v-model="payer.email"
            label="Email"
            type="email"
            variant="outlined"
            density="comfortable"
            :rules="optionalEmailRules"
          />
          <v-text-field autocomplete="one-time-code"
            :model-value="payer.phone"
            label="Телефон"
            variant="outlined"
            density="comfortable"
            :rules="optionalPhoneRules"
            maxlength="16"
            @update:model-value="value => $emit('update-payer-phone', index, value)"
          />
        </div>

        <div class="statement-fields">
          <v-text-field autocomplete="one-time-code" v-model="payer.workPlace" label="Место работы" variant="outlined" density="comfortable" />
          <passport-fields v-model="payer.passport" density="comfortable" :hide-details="false" :required="false" />
          <address-form
            v-model="payer.registrationAddress"
            :token="$model.dadataToken"
            label="Адрес регистрации"
            hint="Начните заполнять адрес регистрации, чтобы получить подсказку"
            variant="outlined"
          />
        </div>
      </div>

      <v-btn class="statement-add-payer" variant="tonal" prepend-icon="mdi-plus" @click="$emit('add-payer')">
        Добавить еще одного родителя
      </v-btn>
    </div>
  </section>
</template>

<script>
import PassportFields from 'moodleDir/procollege/lib/vue/passport.vue'
import AddressForm from 'moodleDir/procollege/lib/vue/address/form.vue'

export default {
  name: 'PayersSection',
  components: {
    PassportFields,
    AddressForm
  },
  emits: ['add-payer', 'remove-payer', 'update-payer-name', 'update-payer-snils', 'update-payer-phone'],
  props: {
    formData: {
      type: Object,
      required: true
    },
    birthDateMin: {
      type: String,
      required: true
    },
    birthDateMax: {
      type: String,
      required: true
    },
    optionalNameRules: {
      type: Array,
      required: true
    },
    optionalSnilsRules: {
      type: Array,
      required: true
    },
    optionalEmailRules: {
      type: Array,
      required: true
    },
    optionalPhoneRules: {
      type: Array,
      required: true
    },
    payersRequired: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /**
     * Normalizes representative codes to strings so Vuetify displays names.
     */
    representativeOptions() {
      const representatives = this.$model && Array.isArray(this.$model.representative)
        ? this.$model.representative
        : []

      return representatives.map(item => ({
        ...item,
        code: String(item.code)
      }))
    }
  }
}
</script>

<style>
.statement-payers {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.statement-payer {
  padding: 16px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #f9fafb;
}

.statement-payer__header {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.statement-payer__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.statement-add-payer {
  align-self: flex-start;
}
</style>
